import { Image } from "expo-image";
import { useContext } from "react";
import type { ImageSourcePropType } from "react-native";
import { View, XStack, YStack, useTheme } from "tamagui";

import CuraiLogo from "@/assets/images/landing_page/general_care/curai-logo.png";
import CuraiLogoDark from "@/assets/images/landing_page/general_care/curai-logo-dark.png";
import ChatInput from "@/components/ChatInput";
import { LayoutContext } from "@/components/LayoutProvider";
import { Text } from "@/components/Typography";
import { useIsDarkTheme } from "@/theme/helpers";

export type WelcomeChip = {
  prefix: string;
  partnerName: string;
  suffix: string;
  backgroundColor: string;
  textColor?: string;
  logo?: ImageSourcePropType;
  logoWidth?: number;
  logoHeight?: number;
  logoTintColor?: string;
};

type GeneralCareHeroProps = {
  welcomeChip?: WelcomeChip;
};

function WelcomeChipView({ chip }: { chip: WelcomeChip }) {
  const theme = useTheme();
  const {
    prefix,
    partnerName,
    suffix,
    backgroundColor,
    textColor = "$neutral0",
    logo,
    logoWidth = 79,
    logoHeight = 15,
    logoTintColor,
  } = chip;
  const tintToken = logoTintColor?.replace(/^\$/, "");
  const resolvedTint = tintToken
    ? theme[tintToken as keyof typeof theme]?.get()
    : undefined;
  return (
    <XStack
      backgroundColor={backgroundColor}
      borderRadius="$200"
      paddingHorizontal="$200"
      paddingVertical="$100"
      alignItems="center"
      gap="$100"
    >
      {logo ? (
        <>
          <Text size="$sm" color={textColor}>
            {prefix}
          </Text>
          <Image
            source={logo}
            style={{ width: logoWidth, height: logoHeight }}
            contentFit="contain"
            tintColor={resolvedTint}
            alt={partnerName}
          />
          <Text size="$sm" color={textColor}>
            {suffix}
          </Text>
        </>
      ) : (
        <Text size="$sm" color={textColor}>
          {prefix}{" "}
          <Text size="$sm" semibold color={textColor}>
            {partnerName}
          </Text>{" "}
          {suffix}
        </Text>
      )}
    </XStack>
  );
}

export default function GeneralCareHero({ welcomeChip }: GeneralCareHeroProps) {
  const { lg } = useContext(LayoutContext);
  const isDark = useIsDarkTheme();
  return (
    <YStack
      alignItems="center"
      paddingHorizontal="$400"
      paddingVertical="$800"
      gap="$200"
      backgroundColor="$backgroundGround"
      width="100%"
      overflow="hidden"
      position="relative"
    >
      <View
        position="absolute"
        pointerEvents="none"
        width={400}
        height={400}
        left="50%"
        top="50%"
        style={
          lg
            ? { transform: [{ translateX: 50 }, { translateY: -200 }] }
            : { transform: [{ translateX: -200 }, { translateY: -200 }] }
        }
      >
        <Image
          source={isDark ? CuraiLogoDark : CuraiLogo}
          style={{ width: "100%", height: "100%" }}
          contentFit="contain"
        />
      </View>

      {welcomeChip ? <WelcomeChipView chip={welcomeChip} /> : null}

      <YStack alignItems="center" gap="$125">
        <Text size="$3xl" textAlign="center">
          Get the care you need in minutes
        </Text>
        <Text size="$lg" textAlign="center" color="$textSecondary">
          Treatment from board-certified clinicians in your state.
        </Text>
      </YStack>

      <View width="100%" maxWidth={500} paddingVertical="$300">
        <ChatInput
          key="chat-input-general-care-hero"
          dataTrackingId="chat-input-general-care-hero"
          extraTrackingContext={{ messageType: "landing-general-care" }}
          placeholder="What can we help you with?"
          animatedStroke
        />
      </View>
    </YStack>
  );
}
